import React from 'react'
import { Button, ConfigProvider, Flex, Form, Input } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'

import { FORM_THEME } from './theme'

/**
 * Username and password entry for a native Cognito user.
 *
 * Credentials are posted straight to Tracer's backend and are never kept beyond
 * the form, or written to storage.
 */
export default function NativeLoginForm({
  onSubmit,
  onForgotPassword,
  busy,
  initialUsername,
}) {
  const intl = useIntl()

  return (
    <ConfigProvider theme={FORM_THEME}>
      <Form
        layout="vertical"
        requiredMark={false}
        disabled={busy}
        initialValues={{ username: initialUsername }}
        onFinish={onSubmit}
        data-testid="native-login-form"
      >
        <Form.Item
          name="username"
          label={<FormattedMessage id="auth.usernameLabel" />}
          rules={[
            {
              required: true,
              whitespace: true,
              message: intl.formatMessage({ id: 'auth.usernameRequired' }),
            },
          ]}
        >
          <Input
            data-testid="login-username"
            placeholder={intl.formatMessage({ id: 'auth.usernamePlaceholder' })}
            autoComplete="username"
            autoFocus={!initialUsername}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={<FormattedMessage id="auth.passwordLabel" />}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'auth.passwordRequired' }),
            },
          ]}
        >
          <Input.Password
            data-testid="login-password"
            placeholder={intl.formatMessage({ id: 'auth.passwordPlaceholder' })}
            autoComplete="current-password"
            autoFocus={Boolean(initialUsername)}
          />
        </Form.Item>

        <Flex vertical={true} gap="small">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            data-testid="login-submit"
            loading={busy}
          >
            <FormattedMessage id={busy ? 'auth.signingIn' : 'auth.signin'} />
          </Button>

          <Flex justify="center">
            <Button
              type="link"
              data-testid="login-forgot-password"
              onClick={onForgotPassword}
            >
              <FormattedMessage id="auth.forgotPassword" />
            </Button>
          </Flex>
        </Flex>
      </Form>
    </ConfigProvider>
  )
}
